import { COMMON_DATE_TYPES } from '@gp/components/RangePicker';
import { SortType } from '@gp/enums/common';
import type {
  IPipelineChangeDetailFetchParams,
  IPipelineChangeDetailFilterConditionsDTO,
  IPipelineChangeDetailFilterConditionsVO,
  IPipelineChangeDetailFilterVO,
  IPipelineChangeDetailTableRecord,
  IPipelineChangeDetailVO,
} from '@gp/types/changeLog';
import { addUuid, getSpecialListValues, sortByCharsPure, toOptions } from '@gp/utils/common';
import { getNullableMoment } from '@gp/utils/date';
import {
  getCascadeProjectDatas,
  getCascadeProjectValues,
  getProjectStageName,
  sortStages,
} from '@gp/utils/project';
import moment from 'moment';
import type { SortBy } from 'tea-component/lib/table/addons';
import { formatTabName, sortTabs } from '../../../utils';
import { DEFAULT_FILTER } from '../models/locker';

const DEFAULT_RECORD: IPipelineChangeDetailTableRecord = {
  project_id: '',
  project: '',
  studio_id: '',
  studio: '',
  platform: [],
  cover: '',
  pteam: '',
  priority: '',
  stage: '',
  project_max_update_time: '',
  tab: '',
  title_id: '',
  title: '',
  field: '',
  old: '',
  new: '',
  update_user: '',
  update_time: '',
};

export const formatFilterConditions = (
  params?: IPipelineChangeDetailFilterConditionsDTO | null,
): {
  values: IPipelineChangeDetailFilterVO;
  conditions: IPipelineChangeDetailFilterConditionsVO;
} => {
  const {
    change_log_time_range: startEndDate = {},
    game_detail_tab: tabConditions,
    change_log_update_user: updateUserConditions,
    stage: stageConditions,
    pteam_bo_studio_project: pteamProject,
  } = params || {};

  const { pteam_list: pteamList = [] } = pteamProject || {};
  const { start_date: minDate, end_date: maxDate } = startEndDate || {};

  const minDateM = getNullableMoment(minDate);
  const endDateM = getNullableMoment(maxDate) || moment();
  const startDateM = !minDateM
    ? moment(endDateM).subtract('days', 6)
    : moment.max(minDateM, moment(endDateM).subtract('days', 6));

  // const pteamConditionsSorted = sortByCharsPure(pteamConditions);
  // const studioConditionsSorted = sortByCharsPure(Object.values(studioMappings || {}));
  const tabConditionsSorted = sortTabs(tabConditions);
  const updateUserConditionsSorted = sortByCharsPure(updateUserConditions);
  const stageConditionsSorted = sortStages(stageConditions);

  // const sortedPteamOptions = toOptions(pteamConditionsSorted);
  // const sortedStudioOptions = mappingToOptions(studioConditionsSorted, studioMappings);
  const sortedTabOptions = toOptions(tabConditionsSorted, formatTabName);
  const sortedUpdateUserOptions = toOptions(updateUserConditionsSorted);
  const sortedStageOptions = toOptions(stageConditionsSorted, getProjectStageName);

  // project配置项及初始化全选
  const cascadeProjectoptions = getCascadeProjectDatas(pteamList, 'project_auth');

  return {
    values: {
      rangePicker: {
        ...DEFAULT_FILTER.rangePicker,
        startDate: startDateM.format('YYYY-MM-DD'),
        endDate: endDateM.format('YYYY-MM-DD'),
      },
      // pteam: pteamConditionsSorted,
      // studio: sortedStudioOptions.map((option) => option?.value),
      tab: tabConditionsSorted,
      update_user: updateUserConditionsSorted,
      stage: stageConditionsSorted,
      project_id: getCascadeProjectValues(cascadeProjectoptions, true),
    },
    conditions: {
      rangePicker: {
        dateTypes: COMMON_DATE_TYPES,
        minDate: minDate || '',
        maxDate: maxDate || '',
      },
      // pteam: sortedPteamOptions,
      // studio: sortedStudioOptions,
      tab: sortedTabOptions,
      update_user: sortedUpdateUserOptions,
      stage: sortedStageOptions,
      project_id: cascadeProjectoptions || [],
    },
    defaultValues: {
      project_id: getCascadeProjectValues(cascadeProjectoptions, false),
    },
  };
};

export const formatFilterFetchParams = (
  payload?: IPipelineChangeDetailFilterVO,
  filterConditions?: IPipelineChangeDetailFilterConditionsVO,
): Omit<
  IPipelineChangeDetailFetchParams,
  'sort' | 'sort_type' | 'sec_sort' | 'sec_sort_type' | 'search_type' | 'search' | 'project_ids'
> => {
  const {
    rangePicker,
    pteam,
    studio,
    tab,
    update_user: updateUser,
    stage,
    project_id,
  } = payload || {};

  const {
    pteam: pteamOptions,
    studio: studioOptions,
    tab: tabOptions,
    update_user: updateUserOptions,
    stage: stageOptions,
  } = filterConditions || {};

  const { startDate, endDate } = rangePicker || {};

  return {
    start_date: startDate || '',
    end_date: endDate || '',
    pteam: getSpecialListValues(pteam, pteamOptions),
    studio: getSpecialListValues(studio, studioOptions),
    tab: getSpecialListValues(tab, tabOptions),
    update_user: getSpecialListValues(updateUser, updateUserOptions),
    stage: getSpecialListValues(stage, stageOptions),
    project_id,
  };
};

export const formatSortFetchParams = (
  sorts?: SortBy<IPipelineChangeDetailTableRecord>[],
): Pick<IPipelineChangeDetailFetchParams, 'sort' | 'sort_type' | 'sec_sort' | 'sec_sort_type'> => {
  const result = {
    sort: '',
    sort_type: SortType.None,
    sec_sort: '',
    sec_sort_type: SortType.None,
  };
  if (!Array.isArray(sorts) || !sorts.length) return result;
  for (const sort of sorts) {
    const { by, order } = sort || {};
    if (!by || !order) continue;
    if (by === 'update_time') {
      result.sort = by;
      result.sort_type = order as SortType;
    } else {
      result.sec_sort = by;
      result.sec_sort_type = order as SortType;
    }
  }
  return result;
};

const totalCount = (data: any) => {
  return data?.tabs?.reduce(
    (sum: any, tab: any) =>
      sum +
      (tab?.titles
        ? tab?.titles?.reduce(
            (titleSum: any, title: any) => titleSum + (title?.fields?.length || 0),
            0,
          )
        : 0),
    0,
  );
};

export const formatChangeDetailRecords = (
  loading?: boolean,
  list?: IPipelineChangeDetailVO[] | null,
  expandItemList?: string[],
  // isExpandAll = false,
): IPipelineChangeDetailTableRecord[] => {
  if (!!loading || !Array.isArray(list) || !list.length) return [];
  const records: IPipelineChangeDetailTableRecord[] = [];

  // 第一层遍历projects
  for (const item of list) {
    const { tabs: tabsTemp = {} as any, ...restItem } = item || {};
    const projectId = item?.project_id || '';
    const isExpand = projectId !== '' && expandItemList?.includes(projectId);
    const filesNum = totalCount(item) || 0;
    let tabs: any = [];
    if (filesNum > 1) {
      tabs = [
        {
          tab: 'ExpandAll',
          tab_auth: true,
          titles: [],
        },
        ...tabsTemp,
      ];
    } else {
      if (tabsTemp) {
        tabs = [...tabsTemp];
      }
    }
    if (!Array.isArray(tabs) || !tabs.length) {
      records.push(addUuid({ ...DEFAULT_RECORD, isExpand, filesNum, ...restItem }));
      continue;
    }

    // 第二层遍历tabs（当前project下的tabs）
    for (const tab of tabs) {
      const { titles, ...restTab } = tab || ({} as any);

      const authObj = {
        [`tab_auth_${tab.tab}`]: tab.tab_auth,
      };
      const restTabValue = {
        ...restTab,
        ...authObj,
      };

      if (!Array.isArray(titles) || !titles.length) {
        records.push(
          addUuid({
            ...DEFAULT_RECORD,
            isExpand,
            filesNum,
            ...restItem,
            ...restTabValue,
            ...authObj,
          }),
        );
        continue;
      }
      // 第三层遍历titles（当前tab下的titles）
      let tabNum = 0;
      for (const title of titles) {
        const { id: titleId, fields, ...restTitle } = title || {};
        if (!Array.isArray(fields) || !fields.length) {
          records.push(
            addUuid({
              ...DEFAULT_RECORD,
              isExpand,
              filesNum,
              ...restItem,
              ...restTabValue,
              ...restTitle,
              title_id: titleId,
            }),
          );
          continue;
        }
        // 第四层遍历fields（当前title下的fields）
        if (Array.isArray(fields) && fields?.length > 0) {
          // eslint-disable-next-line no-loop-func
          fields?.forEach((field: any, index: number) => {
            const fieldAuthObj = {
              [`field_auth_${field.field}`]: field.field_auth,
            };
            const record = addUuid({
              isExpand,
              filesNum,
              ...restItem,
              ...restTabValue,
              ...restTitle,
              ...field,
              title_id: titleId,
              ...fieldAuthObj,
            });
            if (isExpand) {
              records.push(record);
            } else if (index < 1 && tabNum < 1) {
              records.push(record);
              tabNum = tabNum + 1;
            }
          });
        }
      }
    }
  }
  return records;
};

export const getChangeDetailTableRowSpansCfg = (
  loading?: boolean,
  list?: IPipelineChangeDetailTableRecord[] | null,
): [number, number, number][] => {
  if (!!loading || !Array.isArray(list) || !list.length) return [];

  const getSameProjectsLength = (projectId?: string): number => {
    const sameProjects = list.filter((item) => {
      const { project_id: itemProjectId } = item || {};
      return itemProjectId === projectId;
    });
    return sameProjects.length;
  };

  const getSameProjectTabsLength = (projectId: string, tab: string): number => {
    const sameProjectTabs = list.filter((item) => {
      const { project_id: itemProjectId, tab: itemTab } = item || {};
      return itemProjectId === projectId && itemTab === tab;
    });
    return sameProjectTabs.length;
  };

  const getSameProjectTabTitlesLength = (projectId: string, tab: string, title: string): number => {
    const sameProjectTabTitles = list.filter((item) => {
      const { project_id: itemProjectId, tab: itemTab, title: itemTitle } = item || {};
      return itemProjectId === projectId && itemTab === tab && itemTitle === title;
    });
    return sameProjectTabTitles.length;
  };

  return list.map((item, itemIndex) => {
    const { project_id: projectId, tab, title } = item || {};
    const projectRowSpan =
      itemIndex > 0 && projectId === list[itemIndex - 1]?.project_id
        ? 0
        : getSameProjectsLength(projectId);
    const tabRowSpan =
      itemIndex > 0 &&
      projectId === list[itemIndex - 1]?.project_id &&
      tab === list[itemIndex - 1]?.tab
        ? 0
        : getSameProjectTabsLength(projectId, tab);
    const titleRowSpan =
      itemIndex > 0 &&
      projectId === list[itemIndex - 1]?.project_id &&
      tab === list[itemIndex - 1]?.tab &&
      title === list[itemIndex - 1]?.title
        ? 0
        : getSameProjectTabTitlesLength(projectId, tab, title);
    return [projectRowSpan, tabRowSpan, titleRowSpan];
  });
};
